/**
 * V13.25 — Page Cookies factuelle.
 *
 * Contenu basé UNIQUEMENT sur l'inventaire technique réel après désactivation
 * des traceurs (PostHog + emergent-main.js). Aucun nom, durée ou fournisseur
 * inventé.
 *
 * Inventaire au 24/09/2026 (à revérifier périodiquement en navigation privée) :
 *   ── Stockages 1st-party strictement nécessaires ──
 *   • localStorage["token"]              → JWT de session (auth). Suppression = déconnexion.
 *   • localStorage["role"]               → rôle courant (visiteur/athlete/coach/admin/copf).
 *   • localStorage["aito.consent.*"]     → si un jour un consentement traceurs est ajouté.
 *   ── Aucun cookie tiers d'analyse, publicité ou session replay ──
 *   ── Traceurs plateforme Emergent ──
 *   • `emergent-main.js` — retiré du HTML projet. Si la plateforme le
 *     réinjecte, la fiche est mise à jour dès détection.
 */
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Cookie, ShieldCheck, ExternalLink, AlertTriangle } from "lucide-react";

const NECESSARY_STORAGES = [
  {
    name: "token",
    kind: "localStorage",
    purpose: "Jeton de session utilisateur (authentification). Nécessaire pour accéder aux fonctionnalités connectées : éditer sa fiche, consulter le tableau de bord, etc.",
    duration: "Jusqu'à déconnexion manuelle ou expiration du jeton côté serveur",
    provider: "AITO.PF (1st-party)",
  },
  {
    name: "role",
    kind: "localStorage",
    purpose: "Rôle courant de l'utilisateur (visiteur / athlète / coach / administrateur). Permet à l'interface de proposer les bonnes actions et menus.",
    duration: "Jusqu'à déconnexion manuelle",
    provider: "AITO.PF (1st-party)",
  },
];

export default function CookiesPage() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-8" data-testid="cookies-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="shrink-0 h-10 w-10 rounded-full bg-[hsl(var(--aito-lagoon))]/15 border border-[hsl(var(--aito-lagoon))]/30 flex items-center justify-center">
          <Cookie className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" aria-hidden="true" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Cookies et stockages</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Inventaire technique factuel — mis à jour le 24 septembre 2026.
          </p>
        </div>
      </div>

      <Card className="p-5 mb-5 border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
        <div className="flex items-start gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5" aria-hidden="true" />
          <div className="text-sm leading-relaxed">
            <strong className="font-semibold">Aucun traceur d'audience, aucun enregistrement de session,
            aucun cookie publicitaire</strong> ne s'exécute sur le site public AITO.PF avant toute action de l'utilisateur.
            <br />
            Seuls les stockages listés ci-dessous, strictement nécessaires au fonctionnement technique du site
            (authentification), sont utilisés.
          </div>
        </div>
      </Card>

      <h2 className="font-display text-xl font-semibold mt-6 mb-3">Stockages strictement nécessaires</h2>
      <div className="space-y-3">
        {NECESSARY_STORAGES.map((c) => (
          <Card key={c.name} className="p-4" data-testid={`cookie-${c.name}`}>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold">{c.name}</span>
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{c.kind}</span>
            </div>
            <dl className="mt-2 text-sm space-y-1">
              <div><dt className="inline font-medium text-muted-foreground">Finalité : </dt><dd className="inline">{c.purpose}</dd></div>
              <div><dt className="inline font-medium text-muted-foreground">Durée : </dt><dd className="inline">{c.duration}</dd></div>
              <div><dt className="inline font-medium text-muted-foreground">Fournisseur : </dt><dd className="inline">{c.provider}</dd></div>
            </dl>
          </Card>
        ))}
      </div>

      <h2 className="font-display text-xl font-semibold mt-8 mb-3">Ce qui n'est PAS utilisé</h2>
      <ul className="list-disc pl-6 text-sm space-y-1">
        <li>Aucune plateforme d'analyse d'audience (Google Analytics, Matomo, etc.)</li>
        <li>Aucun outil d'enregistrement de session (session replay)</li>
        <li>Aucun cookie publicitaire ni cookie de reciblage</li>
        <li>Aucun cookie de réseau social externe (like/share embarqués)</li>
      </ul>

      <Card className="p-5 mt-8 border-amber-200 bg-amber-50/40 dark:bg-amber-950/20">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" aria-hidden="true" />
          <div className="text-sm leading-relaxed">
            <strong className="font-semibold">Traceurs de la plateforme Emergent</strong> — Le site est hébergé sur
            la plateforme Emergent. Le script <span className="font-mono text-xs">https://assets.emergent.sh/scripts/emergent-main.js</span>
            {" "}peut être injecté au moment du serving par la plateforme. Il a été retiré du code source du projet ;
            si vous constatez un chargement résiduel après désactivation, contactez-nous.
          </div>
        </div>
      </Card>

      <div className="mt-8 text-sm text-muted-foreground">
        Une question, une correction à signaler ? Nous contacter via la page{" "}
        <Link to="/legal/contact" className="text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-1">
          Contact <ExternalLink className="h-3 w-3" />
        </Link>.
      </div>
    </div>
  );
}
